/**
 * Widget V2 — Class session detail
 *
 * Full page for one scheduled class session: what/when/where/price plus the
 * roster of signed-up players when the service opts in (widgetShowAttendees).
 * The roster is server-gated to logged-in viewers — anonymous visitors get a
 * sign-in teaser, never player data.
 */

import React from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { DateTime } from "luxon";

import { LoadingSpinner, EmptyState } from "/imports/ui/components/shared";
import { PageHeading, SECTION_LABEL_SX, DISCOVERY_CARD_SX } from "/imports/ui/components/discovery";
import { PackageOfferHint } from "/imports/ui/components/booking";
import { formatPrice } from "../ClassesView/classHelpers";
import { WaitlistButton, LeaveWaitlistLink, waitlistState, waitlistCaption } from "../ClassesView/components/WaitlistAction";
import useClassSession from "./useClassSession";

// Initials-on-brand-tint avatar for players — mirrors CoachAvatar's fallback
// (initials come pre-built from the API; client photos aren't wired yet).
// The viewer's own avatar gets a primary ring.
function PlayerAvatar({ player, size = 48 }) {
  return (
    <Avatar
      alt={player.displayName}
      sx={{
        width: size,
        height: size,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
        color: "primary.dark",
        fontWeight: 700,
        fontSize: size * 0.36,
        ...(player.isMe && { border: "2px solid", borderColor: "primary.main" })
      }}
    >
      {player.initials || "?"}
    </Avatar>
  );
}

// One roster row: avatar on the left, name + level to the right. A vertical
// list reads every player at once on narrow screens — a horizontal strip
// clips past the third or fourth player and hides the rest behind a scroll.
function PlayerRow({ player, divider }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.25,
        ...(divider && { borderBottom: "1px solid", borderColor: "divider" })
      }}
    >
      <PlayerAvatar player={player} size={44} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 600, fontSize: "0.9rem", ...(player.isMe && { color: "primary.main" }) }}>
          {player.isMe ? `${player.displayName} (You)` : player.displayName}
        </Typography>
        {player.levelName && (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              mt: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.7rem",
              fontWeight: 600,
              lineHeight: 1.4,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.dark"
            }}
          >
            {player.levelName}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function PlayersSection({ attendees, spotsTaken, spotsTotal, isPast, goLogin }) {
  if (!attendees?.enabled) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>
        Who's playing · {spotsTaken} of {spotsTotal}
      </Typography>

      {attendees.requiresLogin ? (
        <Box
          sx={{
            ...DISCOVERY_CARD_SX,
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.04)
          }}
        >
          <LockOutlinedIcon sx={{ color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Log in to see who's playing in this session.
          </Typography>
          <Button size="small" variant="outlined" onClick={goLogin}>
            Log in
          </Button>
        </Box>
      ) : (attendees.players || []).length === 0 ? (
        <Box sx={{ ...DISCOVERY_CARD_SX, p: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            {isPast ? "No one joined this session." : "No one has joined yet — be the first!"}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ ...DISCOVERY_CARD_SX, px: 2, py: 0.5 }}>
          {attendees.players.map((p, i) => (
            <PlayerRow key={i} player={p} divider={i < attendees.players.length - 1} />
          ))}
        </Box>
      )}
    </Box>
  );
}

// Same shape as checkout's main button, so the booking flow reads as one path.
const CTA_SX = { py: 1.25, fontSize: "0.875rem", fontWeight: 700 };

function ClassSessionView() {
  const {
    slug,
    currency,
    cls,
    loading,
    error,
    goBack,
    handleJoin,
    handleJoinWaitlist,
    handleLeaveWaitlist,
    goLogin,
    navigate,
    packageOffer,
    handleViewPackage
  } = useClassSession();

  if (loading) {
    return (
      <Box>
        <PageHeading title="Class session" onBack={goBack} />
        <LoadingSpinner />
      </Box>
    );
  }

  if (error || !cls) {
    return (
      <Box>
        <PageHeading title="Class session" onBack={goBack} />
        <EmptyState
          title="Class not found"
          description="This session is no longer available."
          actionLabel="Back to classes"
          onAction={() => navigate(`/${slug}/classes`)}
        />
      </Box>
    );
  }

  const isFull = cls.spotsAvailable === 0;
  // Reached from the client's booking history, the session may already have
  // started — it reads as a record then, never as something to join.
  const isPast = cls.isPast === true;
  // Started is not finished — a session still on court reads "in progress".
  const isEnded = isPast && cls.isEnded !== false;
  const { eligible, reason, needsAssessment } = cls.eligibility || {};
  const canJoin = !isPast && !isFull && eligible !== false && !cls.isJoined;
  const joinLabel = cls.isJoined
    ? "Joined"
    : isPast
      ? isEnded
        ? "Ended"
        : "Started"
      : isFull
        ? "Full"
        : needsAssessment
          ? "Assess"
          : cls.wasJoined
            ? "Re-join"
            : "Join";
  const dateLabel = cls.date ? DateTime.fromISO(cls.date).toFormat("cccc, MMM d") : "";

  const waitlist = waitlistState(cls);

  // A full session with a waitlist swaps the dead "Full" button for Join waitlist
  // (or Leave, once listed). Not once it has started — the waitlist is over then.
  const joinButton = (fullWidth) =>
    waitlist.show && !isPast ? (
      <WaitlistButton cls={cls} size="medium" fullWidth={fullWidth} onJoin={handleJoinWaitlist} onLeave={handleLeaveWaitlist} sx={CTA_SX} />
    ) : (
      <Box sx={{ display: "flex", flexDirection: fullWidth ? "column" : "row-reverse", alignItems: fullWidth ? "stretch" : "center", gap: 1 }}>
        <Button
          variant={cls.isJoined ? "outlined" : canJoin ? "contained" : "outlined"}
          fullWidth={fullWidth}
          disabled={!canJoin}
          onClick={handleJoin}
          sx={{
            ...CTA_SX,
            ...(!fullWidth && { minWidth: "8rem" }),
            ...(canJoin && { color: "common.white" }),
            ...(cls.isJoined && { borderColor: "success.main", color: "success.main" })
          }}
        >
          {joinLabel}
        </Button>
        {!isPast && <LeaveWaitlistLink cls={cls} onLeave={handleLeaveWaitlist} fullWidth={fullWidth} size="medium" sx={CTA_SX} />}
      </Box>
    );

  return (
    <Box>
      {/* Generic heading — the tinted band below owns the class name. */}
      <PageHeading title="Class session" onBack={goBack} />

      <Box sx={{ ...DISCOVERY_CARD_SX, overflow: "hidden" }}>
        <Box
          sx={{
            p: 3,
            background: (t) => `linear-gradient(120deg, ${alpha(t.palette.primary.main, 0.2)} 0%, ${alpha(t.palette.primary.main, 0.05)} 100%)`
          }}
        >
          <Typography sx={{ fontSize: { xs: "1.3rem", md: "1.6rem" }, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            {cls.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[dateLabel, cls.time, cls.duration ? `${cls.duration} min` : null].filter(Boolean).join(" · ")}
          </Typography>
          {cls.trainer && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              with {cls.trainer.name}
            </Typography>
          )}
          {cls.levelRequiredNames ? (
            <Box mt={1}>
              <Chip
                label={cls.levelRequiredNames}
                size="small"
                variant="outlined"
                color="warning"
                sx={{ bgcolor: "background.paper", fontWeight: 600, boxShadow: "0 1px 4px rgba(26,26,26,0.08)" }}
              />
            </Box>
          ) : null}
        </Box>

        <Box sx={{ p: 3 }}>
          {(cls.facility || cls.location) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <PlaceOutlinedIcon sx={{ fontSize: "1.05rem", color: "primary.main", flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                {cls.facility?.name}
                {cls.facility && cls.location ? " · " : ""}
                {cls.location?.name ? (
                  <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontSize: "1rem" }}>
                    {cls.location.name}
                  </Box>
                ) : null}
              </Typography>
            </Box>
          )}
          {cls.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-line", lineHeight: 1.7 }}>
              {cls.description}
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
            <Box>
              {cls.price != null && (
                <Typography fontWeight={700} lineHeight={1.2} sx={{ fontSize: { xs: "1.05rem", md: "1.25rem" } }}>
                  {formatPrice(cls.price, currency)}
                </Typography>
              )}
              <Typography
                variant="caption"
                color={
                  cls.isJoined
                    ? "success.main"
                    : isPast
                      ? "text.secondary"
                      : waitlist.listed
                        ? "primary.main"
                        : isFull
                          ? "error.main"
                          : "text.secondary"
                }
              >
                {cls.isJoined
                  ? "You're in"
                  : isPast
                    ? isEnded
                      ? "Session ended"
                      : "Session in progress"
                    : waitlistCaption(cls) || (isFull ? "Full" : `${cls.spotsAvailable} spot${cls.spotsAvailable !== 1 ? "s" : ""} left`)}
              </Typography>
            </Box>
            <Box sx={{ display: { xs: "none", md: "block" } }}>{joinButton(false)}</Box>
          </Box>
          {!canJoin && !isPast && reason ? (
            <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }}>
              {reason}
            </Typography>
          ) : null}
          {/* Whether an offer belongs here at all is settled in useClassSession. */}
          {packageOffer ? (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <PackageOfferHint offer={packageOffer} currency={currency} onClick={handleViewPackage} />
              </Box>
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <PackageOfferHint offer={packageOffer} currency={currency} onClick={handleViewPackage} compact />
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>

      <PlayersSection attendees={cls.attendees} spotsTaken={cls.spotsTaken} spotsTotal={cls.spotsTotal} isPast={isPast} goLogin={goLogin} />

      <Box sx={{ mt: 3, display: { md: "none" } }}>{joinButton(true)}</Box>
    </Box>
  );
}

export default ClassSessionView;
